import React from "react";
import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import AnalyticsHeader from "visual-components/analyticsDashboard/analytics-header";
import SocialClicksTooltip from "visual-components/analyticsDashboard/tooltip/SocialClicksTooltip";
import EmptyChartCard from "./EmptyChartCard";
import LabeledBarChart from "./LabeledBarChart";

/**
 * Social Action Clicks Card Component
 * Displays individual social action clicks by their specific titles (not grouped by type)
 * @param {Object} props
 * @param {Array} [props.data=[]] - Array of social actions with titles and click counts
 * @param {boolean} [props.started=false] - Whether the campaign has started
 * @returns {import("react").ReactElement}
 */
export default function SocialActionClicksCard(props) {
  const { data = [], started = false } = props;

  if (!started) {
    return <EmptyChartCard title="Social Action Clicks" message="Campaign hasn't started yet" />;
  }

  if (!data || data.length === 0) {
    return (
      <EmptyChartCard title="Social Action Clicks" message="No social action clicks recorded" />
    );
  }

  // Calculate total clicks
  const totalClicks = data.reduce((sum, item) => sum + item.clicks, 0);

  return (
    <Card sx={{ height: 500 }}>
      <CardHeader
        title={
          <AnalyticsHeader
            title="Social Action Clicks"
            tooltipText="The Social Action Clicks show how many total clicks each specific social action received. These are the individual actions (like a particular brand's like, or follow) listed on the popup that appears after people enter their email on the landing page."
          />
        }
      />
      <CardContent>
        <Box>
          <Typography variant="h5" component="span" sx={{ fontWeight: "bold" }}>
            {totalClicks.toLocaleString()}
          </Typography>
          <Typography variant="h6" component="span" sx={{ ml: 1, color: "text.secondary" }}>
            total clicks
          </Typography>
        </Box>
        <LabeledBarChart
          data={data}
          enabled={started}
          labelField="title"
          valueField="clicks"
          maxItems={20}
          tooltip={(params) => <SocialClicksTooltip {...params} />}
        />
        {data.length > 20 && (
          <Typography variant="caption" color="secondary">
            Showing top 20 of {data.length} social actions
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
